import { useEffect } from 'react'

import { ItemType, type ItemsWithPagination } from '@inno-sdks/files'
import {
  OFFICE_TOOLS_APP_IDS,
  isOfficeToolsGroup
} from 'modules/desktop/shortcutGroups'
import { useTranslation } from 'react-i18next'
import { appConfigs } from 'shared/configs/appConfigs'
import { INNOSCRIPTA_INTERNAL_ORGANIZATION_ID } from 'shared/constants/user'
import { DesktopGridItem } from 'shared/types/desktop-grid'

import { useShortcutGroupStore } from '../../../stores'
import {
  arrangeItemsByType,
  arrangeItemsWithShortcutGroups
} from '../utils/arrange'
import { getOfficeToolsGroupPlacement } from '../utils/officeToolsGroup'

const normalizeUrl = (value: string): string => {
  try {
    return new URL(value, window.location.origin).pathname
  } catch {
    return value
  }
}

/** Groups the five seeded office shortcuts once they are all present. */
export const useProvisionOfficeToolsGroup = ({
  userId,
  organizationId,
  rootFolderItems,
  items,
  cols,
  rows,
  commitManualItems
}: {
  userId?: number
  organizationId?: number
  rootFolderItems?: ItemsWithPagination
  items: DesktopGridItem[]
  cols: number
  rows: number
  commitManualItems: (
    value:
      | DesktopGridItem[]
      | ((prevItems: DesktopGridItem[]) => DesktopGridItem[])
  ) => void
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'desktop.shortcut_group'
  })
  const groups = useShortcutGroupStore(state => state.groups)
  const provisionOfficeToolsGroup = useShortcutGroupStore(
    state => state.provisionOfficeToolsGroup
  )

  useEffect(() => {
    if (
      typeof userId !== 'number' ||
      !rootFolderItems ||
      cols <= 0 ||
      rows <= 0
    )
      return
    // Read the store directly as group hydration may have completed in an
    // earlier effect during this same render.
    const currentGroups = useShortcutGroupStore.getState().groups

    const expectedUrls = OFFICE_TOOLS_APP_IDS.map(
      appId => appConfigs[appId]?.path
    )
    // If an app is not configured in this environment, its default shortcut
    // is not provisioned, so do not create a misleading partial office group.
    if (expectedUrls.some(path => !path)) return

    const expectedUrlSet = new Set(
      expectedUrls.map(path => normalizeUrl(`${window.location.origin}${path}`))
    )
    const officeFileIds = new Set(
      rootFolderItems.data
        .filter(
          item =>
            item.type === ItemType.Shortcut &&
            expectedUrlSet.has(normalizeUrl(item.metaData.shortcutUrl))
        )
        .map(item => item.id.toString())
    )
    if (officeFileIds.size !== expectedUrlSet.size) return

    const officeItems = items.filter(item => officeFileIds.has(item.id))
    if (officeItems.length !== officeFileIds.size) return

    const memberIds = OFFICE_TOOLS_APP_IDS.map(appId => {
      const path = appConfigs[appId]!.path
      const url = normalizeUrl(`${window.location.origin}${path}`)
      const fileId = rootFolderItems.data.find(
        item =>
          item.type === ItemType.Shortcut &&
          normalizeUrl(item.metaData.shortcutUrl) === url
      )?.id
      return fileId?.toString()
    }).filter((id): id is string => id !== undefined)

    if (memberIds.length !== OFFICE_TOOLS_APP_IDS.length) return

    const existingOfficeGroup = currentGroups.find(group =>
      isOfficeToolsGroup(group)
    )
    const placement = existingOfficeGroup
      ? new Map(
          memberIds.map(memberId => {
            const item = items.find(candidate => candidate.id === memberId)
            return [memberId, item?.position] as const
          })
        )
      : getOfficeToolsGroupPlacement({
          memberIds,
          items,
          groups: currentGroups,
          cols,
          rows
        })
    if (!placement) return

    const nextItems = items.map(item => {
      const position = placement.get(item.id)
      return position ? { ...item, position: { ...position } } : item
    })
    const itemsNeedRepair =
      !existingOfficeGroup &&
      nextItems.some(
        (item, index) =>
          item.position.row !== items[index].position.row ||
          item.position.col !== items[index].position.col
      )
    if (itemsNeedRepair) commitManualItems(nextItems)

    const existing = currentGroups.find(group => isOfficeToolsGroup(group))
    const hasSameMemberIds = (first: string[], second: string[]) =>
      first.length === second.length &&
      first.every((memberId, index) => memberId === second[index])
    const needsGroupRepair = existing
      ? existing.title !== t('office_tools') ||
        existing.kind !== 'office-tools' ||
        !hasSameMemberIds(existing.memberIds, memberIds) ||
        currentGroups.some(
          group =>
            !isOfficeToolsGroup(group) &&
            group.memberIds.some(memberId => officeFileIds.has(memberId))
        )
      : true
    if (needsGroupRepair) {
      provisionOfficeToolsGroup(t('office_tools'), memberIds)

      // Newly grouping the office shortcuts changes the desktop's visual
      // balance, so tidy the rest of it. Org 1 is Innoscripta's own,
      // internally-curated desktop, which manages its own layout.
      if (
        !existingOfficeGroup &&
        organizationId !== INNOSCRIPTA_INTERNAL_ORGANIZATION_ID
      ) {
        const updatedGroups = useShortcutGroupStore.getState().groups
        commitManualItems(prevItems =>
          arrangeItemsWithShortcutGroups(
            arrangeItemsByType,
            prevItems,
            cols,
            rows,
            updatedGroups
          )
        )
      }
    }
  }, [
    cols,
    commitManualItems,
    groups,
    items,
    organizationId,
    rootFolderItems,
    rows,
    provisionOfficeToolsGroup,
    t,
    userId
  ])
}
